<template>
  <div id="rg-object-history">
    <div :id="mapId" class="rg-object-history-map"></div>
    <rg-date-input-dialog
      :active="isActiveDateInputDialog"
      @sendInputValue="setUpPlayback(calcInputDateTime({ startedAt: $event }))"
      @cancel="onCancel"
    />
    <rg-playback-controller
      :playbackRequestSettings="playbackRequestSettings"
    ></rg-playback-controller>
  </div>
</template>

<script>
import "mapbox-gl/dist/mapbox-gl.css";
import Mapbox from "mapbox-gl";
import RgPlaybackController from "./components/RgPlaybackController";
import RgDateInputDialog from "./components/RgDateInputDialog.vue";
import { MAP_STYLE_DEFAULT } from "@/config/const";
import { createNamespacedHelpers } from "vuex";
import C from "./Constants";
import moment from "moment-timezone";
import * as turf from "@turf/turf";
import isDate from "lodash/isDate";
import rgPlayBackService from "@/services/rgPlayBackService";

const {
  mapActions: mapActionsOfRGPlayback,
  mapGetters: mapGettersOfRGPlayback,
  mapMutations: mapMutationsOfRGPlayback,
} = createNamespacedHelpers("radarGridPlayback");

export default {
  name: "RgObjectHistoryMap",
  components: {
    RgPlaybackController,
    RgDateInputDialog,
  },
  props: {
    mapId: {
      type: String,
      required: true,
    },
    // mapbox initialize setting
    mapSetting: {
      type: Object,
      required: true,
    },
    mapStyle: {
      type: String,
      default: MAP_STYLE_DEFAULT,
    },
    updateMapStyle: {
      type: Function,
      required: true,
    },
    mapLocale: {
      type: String,
      default: null,
    },
    textLayerIds: {
      type: Array, // Array<String>
      default() {
        return [];
      },
    },
    playbackRequestSettings: {
      type: Object, // playbackRequestSettings
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      map: null,
      isStyleReady: false,
      requestAnimationId: null,
      defaultOffSet: C.PLAYBACK_DATE_OFFSET,
      isUseLayerFilter: true,
    };
  },
  watch: {
    mapStyle(style) {
      if (this.isMapReady) {
        this.map.setStyle(style);
      }
    },
    playbackStatus() {
      this.animate();
    },
    visibleTrackVessels() {
      this.animate();
    },
    async playbackTimeNow() {
      if (this.isPlayBackStatusPlay) return;
      this.animate();
    },
  },
  computed: {
    ...mapGettersOfRGPlayback([
      "isInitial",
      "playbackBaseTime",
      "playbackTimeNow",
      "playbackStartTime",
      "playbackEndTime",
      "playbackStatus",
      "playbackSecondsPerFrame",
      "isVisibleTrack",
      "visibleTracks",
      "visibleTrackVessels",
      "hasRgPlaybackObjects",
      "isActiveDateInputDialog",
    ]),

    isMapReady() {
      return this.map instanceof Mapbox.Map;
    },

    isPlayBackStatusPlay() {
      return this.playbackStatus === C.PLAYBACK_STATUS.PLAY;
    },

    isPlayBackStatusPause() {
      return this.playbackStatus === C.PLAYBACK_STATUS.PAUSE;
    },

    computedLayerAreaFilter() {
      if (!this.isUseLayerFilter) return ["all"]; // この設定はfilterに特に影響しない
      // エリア内のみ表示するfilter
      return [
        "all",
        [">=", "lon", this.playbackRequestSettings._westLongitude],
        ["<=", "lon", this.playbackRequestSettings._eastLongitude],
        ["<=", "lat", this.playbackRequestSettings._northLatitude],
        [">=", "lat", this.playbackRequestSettings._southLatitude],
      ];
    },
  },
  methods: {
    ...mapActionsOfRGPlayback([
      "getAisVesselsHistory",
      "generateCurrentGeoJson",
      "activateDateInputDialog",
      "resetVisibleTracks",
      "resetState",
    ]),
    ...mapMutationsOfRGPlayback([
      "SET_IS_INITIAL",
      "SET_PLAYBACK_SETTINGS",
      "SET_PLAYBACK_TIME_NOW",
      "SET_VISIBLE_TRACKS",
      "SET_RG_PLAYBACK_STATUS",
    ]),

    setUp() {
      this.map = this.initMap();
      if (!this.playbackRequestSettings.isAreaValid()) {
        // 海域指定が不適切な場合は海域指定に戻る
        this.playbackRequestSettings.toAreaSetting(this.$router);
      } else if (!this.playbackRequestSettings.isPeriodValid()) {
        this.activateDateInputDialog(); // 時間指定が不適切な場合はカレンダーを表示
      } else {
        this.setUpPlayback(this.playbackRequestSettings.calcPeriod());
      }
    },

    cleanup() {
      window.cancelAnimationFrame(this.requestAnimationId);
      this.resetState();
    },

    initMap() {
      const { mapId, mapSetting, mapStyleLoad, mapLoad } = this;
      Mapbox.accessToken = process.env.VUE_APP_MAPBOX_TOKEN;
      const map = new Mapbox.Map({
        container: mapId,
        ...mapSetting,
        style: this.mapStyle,
      });

      map.addControl(
        new Mapbox.ScaleControl({
          maxWidth: 80,
          unit: "metric",
        })
      );
      map.addControl(new Mapbox.NavigationControl(), "bottom-left");
      map.on("style.load", mapStyleLoad);
      map.on("load", mapLoad);
      return map;
    },

    async mapLoad() {
      this.map.resize();
      if (this.isInitial) {
        await this.setLayers();
        this.setLayerClickEvents();
        this.setMousePointer();
        this.setMapLayerPopup();
        this.SET_IS_INITIAL(false);
      } else {
        this.resetVisibleTracks();
      }
    },

    async mapStyleLoad(_a, _b) {
      if (!this.map) return;
      this.isStyleReady = false;
      if (this.mapStyle === MAP_STYLE_DEFAULT) {
        this.textLayerIds.forEach(id => {
          this.map.setLayoutProperty(id, "text-field", ["get", this.mapLocale]);
        });
      }
      this.isStyleReady = true;
    },

    // ダイアログでキャンセル時、期間指定が無効だとエリア設定に戻る
    onCancel() {
      this.SET_RG_PLAYBACK_STATUS(C.PLAYBACK_STATUS.PAUSE);
      if (!this.playbackRequestSettings.isPeriodValid()) {
        this.playbackRequestSettings.toAreaSetting(this.$router);
      }
    },

    calcInputDateTime(
      {
        startedAt, // Date utc
        endedAt, // Date utc
      },
      offSet = this.defaultOffSet
    ) {
      if (!isDate(startedAt)) return null;
      return {
        startedAt, // Date
        endedAt: isDate(endedAt)
          ? endedAt
          : moment(startedAt)
              .utc()
              .add(offSet, "hour")
              .toDate(), // Date
      };
    },

    async setUpPlayback(
      {
        startedAt, // Date utc
        endedAt, // Date utc
      } // inputDateTime
    ) {
      try {
        this.$vs.loading({
          container: "#rg-object-history",
          scale: 0.6,
        });

        // プログレスバーの初期化
        const mStartTime = moment(startedAt).utc();
        const mEndTime = moment(endedAt).utc();
        this.SET_PLAYBACK_SETTINGS({
          baseTime: mStartTime.unix(),
          startTime: mStartTime.unix(),
          endTime: mEndTime.unix(),
          now: mStartTime.unix(),
          secondsPerFrame: this.playbackSecondsPerFrame || 1,
          playbackStatus: C.PLAYBACK_STATUS.PAUSE,
        });

        // URLへのパラメータ設定
        this.setRequestSettingToRoute(
          rgPlayBackService.createRgPlaybackSettings({
            ...this.playbackRequestSettings.calcPlaybackRequestSettingParams(),
            started_at: startedAt.toISOString(),
            ended_at: endedAt.toISOString(),
          })
        );

        const [
          west,
          south,
          east,
          north,
        ] = this.playbackRequestSettings.calcBufferedBoundingBox();
        // データ取得
        await this.getAisVesselsHistory({
          started_at: startedAt.toISOString(),
          ended_at: endedAt.toISOString(),
          north_east_latitude: north,
          north_east_longitude: east,
          south_west_latitude: south,
          south_west_longitude: west,
        });

        if (!this.hasRgPlaybackObjects) {
          // 200でデータ無し -> 通知
          this.$vs.notify({
            text: C.PLAYBACK_ERROR_MESSAGES.DATA_NOT_EXISTS,
            color: "danger",
          });
        }

        this.resetVisibleTracks();
        this.SET_PLAYBACK_TIME_NOW(this.playbackBaseTime);
        await this.animate(); // 描画へ進める
        // }
      } catch (e) {
        // 500、401、403 -> 共通エラーメッセージ
        this.$vs.notify({
          text: C.PLAYBACK_ERROR_MESSAGES.DATA_FETCH_FAILED,
          color: "danger",
        });
      } finally {
        this.$vs.loading.close("#rg-object-history > .con-vs-loading");
      }
    },

    setRequestSettingToRoute(nextPlaybackRequestSettings) {
      // 指定海域・指定期間が変わったときはアドレスバーを更新
      if (!this.playbackRequestSettings.isEqual(nextPlaybackRequestSettings)) {
        this.$router.push({
          query: nextPlaybackRequestSettings.calcPlaybackRequestSettingParams(),
        });
      }
    },

    requestAnimation(e) {
      if (e.dataType === "source") {
        this.map
          .getSource(C.SOURCE_ID.RG_OBJECT_POINTS)
          .off("data", this.requestAnimation);
        this.requestAnimationId = window.requestAnimationFrame(this.animate);
      }
    },

    async animate() {
      if (this.playbackStatus === C.PLAYBACK_STATUS.PLAY) {
        this.SET_PLAYBACK_TIME_NOW(
          this.playbackTimeNow + this.playbackSecondsPerFrame
        );
        if (this.map.getSource(C.SOURCE_ID.RG_OBJECT_POINTS)) {
          this.map
            .getSource(C.SOURCE_ID.RG_OBJECT_POINTS)
            .on("data", this.requestAnimation);
        }
      }
      if (!this.map.getSource(C.SOURCE_ID.RG_OBJECT_POINTS)) return;
      if (!this.map.getSource(C.SOURCE_ID.RG_OBJECT_TRACKS)) return;
      const {
        points = {},
        tracks,
        vectors,
      } = await this.generateCurrentGeoJson();
      this.map.getSource(C.SOURCE_ID.RG_OBJECT_POINTS).setData(points);
      this.map.getSource(C.SOURCE_ID.RG_OBJECT_TRACKS).setData(tracks);
      this.map.getSource(C.SOURCE_ID.RG_OBJECT_VECTORS).setData(vectors);
    },

    calcDisplayArea() {
      return this.playbackRequestSettings.calcPolygon();
    },

    async setLayers() {
      // 表示エリアレイヤー
      const displayArea = this.calcDisplayArea();
      this.map.addSource("rg-object-display-area", {
        type: "geojson",
        data: displayArea,
      });

      this.map.addLayer({
        id: C.LAYER_ID.RG_DISPLAY_AREA_LAYER,
        type: "line",
        source: C.SOURCE_ID.RG_OBJECT_DISPLAY_AREA,
        layout: {},
        paint: {
          "line-color": "#BDBDBD",
          "line-width": 1,
        },
      });

      //現在のGeoJson
      let currentGeoJson = await this.generateCurrentGeoJson();

      //航跡GeoJson
      this.map.addSource(C.SOURCE_ID.RG_OBJECT_TRACKS, {
        type: "geojson",
        data: currentGeoJson.tracks,
      });

      //航跡レイヤー
      this.map.addLayer({
        id: C.LAYER_ID.RG_HISTORY_TRACK_LAYER,
        type: "line",
        source: C.SOURCE_ID.RG_OBJECT_TRACKS,
        paint: {
          "line-color": ["get", "color"],
          "line-width": 2.0,
          "line-opacity": ["get", "opacity"],
          "line-dasharray": [1, 1],
        },
        filter: ["all", ["==", "visible", true], this.computedLayerAreaFilter],
      });

      // 航跡のマウスイベント用レイヤー
      this.map.addLayer({
        id: C.LAYER_ID.RG_HISTORY_TRACK_HOVER_EVENT_LAYER,
        type: "line",
        source: C.SOURCE_ID.RG_OBJECT_TRACKS,
        paint: {
          "line-width": 20.0,
          "line-opacity": 0.0,
        },
        filter: [
          "all",
          ["==", "visible", true],
          [
            "any",
            ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.MY_VESSELS],
            ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.OTHER_VESSELS],
          ],
          this.computedLayerAreaFilter,
        ],
      });

      //位置情報のGeoJson
      this.map.addSource(C.SOURCE_ID.RG_OBJECT_POINTS, {
        type: "geojson",
        data: currentGeoJson.points,
      });

      // AIS船レイヤー
      this.map.addLayer({
        id: C.LAYER_ID.RG_VESSEL_SYMBOL_LAYER,
        type: "symbol",
        source: C.SOURCE_ID.RG_OBJECT_POINTS,
        maxzoom: 14,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "text-allow-overlap": true,
          "text-optional": true,
          "icon-image": "{icon}",
          "icon-rotate": {
            type: "identity",
            property: "heading",
          },
        },
        filter: [
          "all",
          [
            "any",
            ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.MY_VESSELS],
            ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.OTHER_VESSELS],
          ],
          this.computedLayerAreaFilter,
        ],
      });

      this.map.addLayer({
        id: C.LAYER_ID.RG_VESSEL_POLYGON_LAYER,
        type: "fill",
        minzoom: 14,
        source: C.SOURCE_ID.RG_OBJECT_POINTS,
        paint: {
          "fill-color": {
            type: "identity",
            property: "co",
          },
        },
        filter: [
          "all",
          ["==", "hasSize", true],
          [
            "any",
            ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.MY_VESSELS],
            ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.OTHER_VESSELS],
          ],
          this.computedLayerAreaFilter,
        ],
      });

      this.map.addLayer({
        id: C.LAYER_ID.RG_VESSEL_NO_POLYGON_LAYER,
        type: "symbol",
        minzoom: 14,
        source: C.SOURCE_ID.RG_OBJECT_POINTS,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{icon}",
          "icon-rotate": {
            type: "identity",
            property: "heading",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "opacity",
          },
        },
        filter: [
          "all",
          ["==", "hasSize", false],
          [
            "any",
            ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.MY_VESSELS],
            ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.OTHER_VESSELS],
          ],
          this.computedLayerAreaFilter,
        ],
      });

      this.map.addLayer({
        id: C.LAYER_ID.RG_VESSEL_TEXT_LAYER,
        type: "symbol",
        source: C.SOURCE_ID.RG_OBJECT_POINTS,
        maxzoom: 24,
        minzoom: 10,
        layout: {
          "text-field": ["get", "vesselName"],
          "text-anchor": "top",
          "text-size": 14,
          "text-max-width": 30,
          "text-line-height": 1.2,
          "text-offset": [0, 1.1],
          "text-allow-overlap": false,
          "text-ignore-placement": false,
          "text-justify": "left",
        },
        paint: {
          "text-color": "#FFFFFF",
          "text-halo-color": "#000000",
          "text-halo-width": 1,
        },
        filter: [
          "all",
          [
            "any",
            ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.MY_VESSELS],
            ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.OTHER_VESSELS],
          ],
          this.computedLayerAreaFilter,
        ],
      });

      this.map.addLayer({
        id: C.LAYER_ID.RG_VESSEL_DESCRIPTION_LAYER,
        type: "symbol",
        source: C.SOURCE_ID.RG_OBJECT_POINTS,
        maxzoom: 24,
        minzoom: 10,
        layout: {
          "text-field": [
            "format",
            ["get", "vesselName"], // 船舶名がない時はUNKNOWNと表示
            "\n速力 : ",
            ["coalesce", ["get", "speed"], "---"],
            "kn",
            "\n方位 : ",
            ["coalesce", ["get", "headingText"], "---"],
            "°",
            "\n船種 : ",
            ["coalesce", ["get", "category"], "---"],
            "\nサイズ : ",
            ["coalesce", ["get", "length"], "---"],
            "m",
            " x ",
            ["coalesce", ["get", "width"], "---"],
            "m",
          ],
          "text-anchor": "top",
          "text-size": 14,
          "text-max-width": 30,
          "text-line-height": 1.2,
          "text-offset": [0, 1.2],
          "text-allow-overlap": false,
          "text-ignore-placement": false,
          "text-justify": "left",
        },
        paint: {
          "text-color": "#FFFFFF",
          "text-halo-color": "#000000",
          "text-halo-width": 1,
        },
        filter: false, // 初期表示時は非表示、表示する場合はsetFilterVesselDescriptionで表示する
      });

      // 自船マーカー
      this.map.addLayer({
        id: C.LAYER_ID.RG_VESSEL_MARKER_LAYER,
        type: "symbol",
        source: C.SOURCE_ID.RG_OBJECT_POINTS,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-image": "{marker}",
          "icon-offset": [0, -15],
        },
        filter: [
          "all",
          ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.MY_VESSELS],
          this.computedLayerAreaFilter,
        ],
      });

      // vector
      this.map.addSource(C.SOURCE_ID.RG_OBJECT_VECTORS, {
        type: "geojson",
        data: currentGeoJson.vectors,
      });

      this.map.addLayer(
        {
          id: C.LAYER_ID.RG_VESSEL_VECTOR_LAYER,
          type: "line",
          source: C.SOURCE_ID.RG_OBJECT_VECTORS,
          minzoom: 10,
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#DA514B",
            "line-width": 2.0,
            "line-opacity": 1.0, // 履歴のvectorは時間によって色の濃さは変えない
          },
          filter: this.computedLayerAreaFilter,
        },
        C.LAYER_ID.RG_VESSEL_SYMBOL_LAYER // 船の上にvectorが出ないようにする
      );
    },

    setLayerClickEvents() {
      let self = this;
      // 船舶レイヤーのクリックイベント
      let onClickVessel = evt => {
        //航跡の表示設定
        let props = evt.features[0].properties;
        let copyVisibleTracks = JSON.parse(JSON.stringify(self.visibleTracks));
        let vesselId = props.vesselId || props.id;
        if (this.isVisibleTrack(props.objectType, vesselId)) {
          const newVisibleTracks = copyVisibleTracks.vessels.filter(
            v => v !== vesselId
          );
          copyVisibleTracks.vessels = newVisibleTracks;
          this.SET_VISIBLE_TRACKS(copyVisibleTracks);
        } else {
          copyVisibleTracks.vessels.push(vesselId);
          this.SET_VISIBLE_TRACKS(copyVisibleTracks);
        }
        this.setFilterVesselDescription(copyVisibleTracks.vessels);
      };
      this.map.on("click", C.LAYER_ID.RG_VESSEL_SYMBOL_LAYER, onClickVessel);
      this.map.on("click", C.LAYER_ID.RG_VESSEL_POLYGON_LAYER, onClickVessel);
      this.map.on(
        "click",
        C.LAYER_ID.RG_VESSEL_NO_POLYGON_LAYER,
        onClickVessel
      );
    },

    setFilterVesselDescription(
      vesselIds // string[]
    ) {
      if (!this.map) return;
      this.map.setFilter(C.LAYER_ID.RG_VESSEL_DESCRIPTION_LAYER, [
        "all",
        ["in", "vesselId", ...vesselIds],
        [
          "any",
          ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.MY_VESSELS],
          ["==", "objectType", C.PLAYBACK_OBJECT_TYPE.OTHER_VESSELS],
        ],
        this.computedLayerAreaFilter,
      ]);
    },

    setMousePointer() {
      let self = this;
      const keys = Object.keys(C.LAYER_ID);
      keys.forEach(key => {
        if (
          C.LAYER_ID.RG_VESSEL_SYMBOL_LAYER === C.LAYER_ID[key] ||
          C.LAYER_ID.RG_VESSEL_POLYGON_LAYER === C.LAYER_ID[key] ||
          C.LAYER_ID.RG_VESSEL_NO_POLYGON_LAYER === C.LAYER_ID[key]
        ) {
          this.map.on("mouseenter", C.LAYER_ID[key], () => {
            self.map.getCanvas().style.cursor = "pointer";
          });
          this.map.on("mouseleave", C.LAYER_ID[key], () => {
            self.map.getCanvas().style.cursor = "";
          });
        }
      });
    },

    setMapLayerPopup() {
      const { RG_HISTORY_TRACK_HOVER_EVENT_LAYER } = C.LAYER_ID;
      const mapCanvas = this.map.getCanvas();
      let popup = null;

      const createPopup = properties => {
        const { lat, lon, speed, epoch, name } = properties;

        const speedLabel = this.$t("Views.Vessel.Velocity");
        const dataTimeLabel = this.$t("Views.Commons.DataTime");
        const wakeLineLabel = this.$t("Views.Vessel.WakeLine");
        const latitudeLabel = this.$t("Views.Vessel.Latitude");
        const longitudeLabel = this.$t("Views.Vessel.Longitude");

        const roundedSpeed = (Math.round(speed * 10 ** 1) / 10 ** 1).toFixed(1);
        const dataTime = moment.unix(epoch).format("YYYY/MM/DD HH:mm:ss");
        const flooredLatitude = (Math.trunc(lat * 10 ** 8) / 10 ** 8).toFixed(
          8
        );
        const flooredLongitude = (Math.trunc(lon * 10 ** 8) / 10 ** 8).toFixed(
          8
        );

        return new Mapbox.Popup({
          className: `${C.RG_WAKE_LINE_POPUP_WRAPPER_CLASS_NAME}`,
          closeButton: false,
          closeOnClick: false,
        }).setHTML(
          `<strong>${name}</strong><span>の${wakeLineLabel}</span><br>
           <span class="mr-2">${speedLabel}: <strong>${roundedSpeed} kn</strong></span><br>
           <span>${dataTimeLabel}: <strong>${dataTime}</strong></span><br>
           <span>${latitudeLabel}: <strong>${flooredLatitude}</strong></span><br>
           <span>${longitudeLabel}: <strong>${flooredLongitude}</strong></span>
          `
        );
      };
      const removePopup = () => {
        if (!popup) return;
        if (typeof popup.remove === "function") {
          popup.remove();
        }
      };
      /**
       * イベントに最も近いフィーチャーを計算します。
       * @param {Object} e - mapboxのイベントオブジェクト
       * @param {Array} layerIds - 計算対象のレイヤーIDの配列
       * @returns {Object} - 最も近いフィーチャーオブジェクト
       */
      const calculateClosestFeatureToEvent = (e, layerIds) => {
        const { point, lngLat } = e;
        const eventFeatures = this.map.queryRenderedFeatures(point, {
          layers: layerIds,
        });

        const pointFeature = turf.point([lngLat.lng, lngLat.lat]);
        const distances = eventFeatures.map(feature => {
          return turf.pointToLineDistance(pointFeature, feature);
        });
        const closestDistance = Math.min(...distances);
        const closestDistanceIndex = distances.indexOf(closestDistance);

        return eventFeatures[closestDistanceIndex];
      };

      // ホバー時の処理を付与
      [RG_HISTORY_TRACK_HOVER_EVENT_LAYER].forEach(layerId => {
        this.map.on("mousemove", layerId, e => {
          if (e.defaultPrevented) return; // 同じイベントで複数回のcallbackが実行されるのを防ぐ
          e.preventDefault();

          removePopup();
          if (!this.isPlayBackStatusPause) return; // 再生中はポップアップを表示しない

          mapCanvas.style.cursor = "pointer";
          const feature = calculateClosestFeatureToEvent(e, [
            RG_HISTORY_TRACK_HOVER_EVENT_LAYER,
          ]);
          const { lng, lat } = e.lngLat;
          popup = createPopup(feature.properties);
          popup.setLngLat([lng, lat]).addTo(this.map);
        });
        this.map.on("mouseleave", layerId, () => {
          mapCanvas.style.cursor = "";
          removePopup();
        });
      });
    },
  },
  async mounted() {
    this.setUp();
  },
  async beforeDestroy() {
    this.cleanup();
  },
};
</script>

<style lang="scss" scoped>
.rg-object-history-map {
  width: 100vw;
  min-height: 100vh; /* Fallback */
  min-height: calc(var(--vh, 1vh) * 100);

  position: relative;
  ::v-deep .mapboxgl-ctrl-logo {
    display: none !important;
  }
  ::v-deep .rg-wake-line-popup-wrapper {
    z-index: 11; // 詳細パネルと重ならないようにする
  }
}
</style>
